import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../utils";
import { RootState } from "../../redux/store";
import {
  CardsList,
  CardsListApi,
  ExportLabelType,
  GenerateShipmentPayload,
  labelDownloadType,
  ShipmentsList,
  UnblockPinOfflinePayload,
} from "./cardsApi";
import axios from "axios";

export interface CardsState {
  cardsListStatus: QueryStatus;
  cardsList: CardsList[];
  shipmentsListStatus: QueryStatus;
  shipmentsList: ShipmentsList[];
  loading: boolean;
  error: string | null;
  labelExportStatus: QueryStatus;
  labelDownloadStatus: QueryStatus;
  generateShipmentStatus: QueryStatus;
  unblockPinOfflineStatus: QueryStatus;
}

const initialState: CardsState = {
  cardsListStatus: "idle",
  cardsList: [],
  shipmentsListStatus: "idle",
  shipmentsList: [],
  loading: false,
  error: null,
  labelExportStatus: "idle",
  labelDownloadStatus: "idle",
  generateShipmentStatus: "idle",
  unblockPinOfflineStatus: "idle",
};

export const cardsListAsync = createAsyncThunk(
  "cardsList/call",
  async (payload: { companyUuid: string }) => {
    const axios = authAxios();
    const response = await axios.get<Array<CardsListApi>>(
      `cards/company/${payload.companyUuid}`,
    );
    return response.data
      .map((payload) => ({
        ...payload,
        address: {
          ...payload.address,
          addressLine2: payload.address.addressLine2 ?? null,
        },
        vehicleReference: payload.vehicleReference?.toLocaleUpperCase() ?? null,
        addressDisplayed: `${payload.address.addressLine1}${payload.address.addressLine2 ? ` ${payload.address.addressLine2}` : ""}, ${payload.address.postalCode} ${payload.address.city} ${payload.address.country}`,
      }))
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  },
);

export const shipmentsListAsync = createAsyncThunk(
  "shipmentsList/call",
  async () => {
    const axios = authAxios();
    const response = await axios.get<ShipmentsList[]>("cards/shipments");
    return response.data;
  },
);

export const generateShipmentAsync = createAsyncThunk(
  "generateShipment/call",
  async (payload: GenerateShipmentPayload, thunkAPI) => {
    const axios = authAxios();
    await axios.post("cards/generate_shipment", payload);
    await thunkAPI.dispatch(
      pollCards({
        cardsToCheckUuid: payload.cardsUuid,
        companyUuid: payload.companyUuid,
      }),
    );
  },
);

export const labelExportAsync = createAsyncThunk(
  "depositExport/call",
  async (payload: ExportLabelType, thunkAPI) => {
    const axios = authAxios();
    const response = await axios.get<{ presignedUrl: string | null }>(
      `cards/shipment/${payload.uuid}/export`,
    );
    const presignedUrl = response.data.presignedUrl;
    if (!presignedUrl) {
      return;
    }
    const bucketFileName = `${payload.uuid}.pdf`;
    void thunkAPI.dispatch(
      labelDownload({
        presignedUrl,
        bucketFileName,
      }),
    );
  },
);

export const unblockPinOfflineAsync = createAsyncThunk(
  "unblockPinOffline/call",
  async (payload: UnblockPinOfflinePayload) => {
    const axios = authAxios();
    await axios.post(`card/${payload.cardUuid}/unblock_pin_offline`, {
      pin: payload.pin.trim(),
      updatedBy: payload.updatedBy.trim(),
    });
  },
);

//TODO TOASTER ?
export const labelDownload = createAsyncThunk(
  "depositDownload/call",
  async (paylaod: labelDownloadType) => {
    if (paylaod.presignedUrl && paylaod.bucketFileName) {
      const response = await axios({
        url: paylaod.presignedUrl,
        method: "GET",
        responseType: "blob",
      });
      const a = document.createElement("a");
      a.download = paylaod.bucketFileName || "yourLabelFile.pdf";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      a.href = url;
      a.click();
    }
  },
);

export const pollCards = createAsyncThunk(
  "cards/pollCards",
  async (
    payload: { cardsToCheckUuid: string[]; companyUuid: string },
    { rejectWithValue, dispatch },
  ) => {
    const maxTime = 15000;
    const interval = 1000;
    const startTime = Date.now();

    const poll = async (): Promise<void> => {
      try {
        const response = await dispatch(
          cardsListAsync({ companyUuid: payload.companyUuid }),
        );
        const cardsFetch = response.payload as CardsList[];

        // condition : toutes les cartes ont shipment rempli
        const cardsToCheck = cardsFetch.filter((card) =>
          payload.cardsToCheckUuid?.includes(card.uuid),
        );
        const allReady = cardsToCheck.every(
          (c) => c.shipment !== null && c.shipment !== undefined,
        );

        if (allReady) {
          console.log("ok");
          return;
        }

        if (Date.now() - startTime > maxTime) {
          throw new Error("Timeout reached");
        }

        // attendre et relancer
        await new Promise((resolve) => setTimeout(resolve, interval));
        return poll();
      } catch (err: unknown) {
        rejectWithValue(err as string);
      }
    };

    return poll();
  },
);

export const cardsSlice = createSlice({
  name: "cards",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetCardsListStatus(state) {
      state.cardsListStatus = "idle";
    },
    resetGenerateShipmentStatus(state) {
      state.generateShipmentStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(cardsListAsync.pending, (state) => {
        state.cardsListStatus = "processing";
      })
      .addCase(cardsListAsync.fulfilled, (state, action) => {
        state.cardsList = action.payload;
        state.cardsListStatus = "success";
      })
      .addCase(cardsListAsync.rejected, (state) => {
        state.cardsListStatus = "failed";
      })
      .addCase(pollCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(pollCards.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(pollCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(shipmentsListAsync.pending, (state) => {
        state.shipmentsListStatus = "processing";
      })
      .addCase(shipmentsListAsync.fulfilled, (state, action) => {
        state.shipmentsList = action.payload;
        state.shipmentsListStatus = "success";
      })
      .addCase(shipmentsListAsync.rejected, (state) => {
        state.shipmentsListStatus = "failed";
      })
      .addCase(labelExportAsync.pending, (state) => {
        state.labelExportStatus = "processing";
      })
      .addCase(labelExportAsync.fulfilled, (state) => {
        state.labelExportStatus = "success";
      })
      .addCase(labelExportAsync.rejected, (state) => {
        state.labelExportStatus = "failed";
      })
      .addCase(labelDownload.pending, (state) => {
        state.labelDownloadStatus = "processing";
      })
      .addCase(labelDownload.fulfilled, (state) => {
        state.labelDownloadStatus = "success";
      })
      .addCase(labelDownload.rejected, (state) => {
        state.labelDownloadStatus = "failed";
      })
      .addCase(generateShipmentAsync.pending, (state) => {
        state.generateShipmentStatus = "processing";
      })
      .addCase(generateShipmentAsync.fulfilled, (state) => {
        state.generateShipmentStatus = "success";
      })
      .addCase(generateShipmentAsync.rejected, (state) => {
        state.generateShipmentStatus = "failed";
      })
      .addCase(unblockPinOfflineAsync.pending, (state) => {
        state.unblockPinOfflineStatus = "processing";
      })
      .addCase(unblockPinOfflineAsync.fulfilled, (state) => {
        state.unblockPinOfflineStatus = "success";
      })
      .addCase(unblockPinOfflineAsync.rejected, (state) => {
        state.unblockPinOfflineStatus = "failed";
      });
  },
});

export const selectCardsList = (state: RootState) => state.cards.cardsList;

export const selectCardsListStatus = (state: RootState) =>
  state.cards.cardsListStatus;

export const selectShipmentsList = (state: RootState) =>
  state.cards.shipmentsList;

export const selectShipmentsListStatus = (state: RootState) =>
  state.cards.shipmentsListStatus;

export const selectGenerateShipmentStatus = (state: RootState) =>
  state.cards.generateShipmentStatus;

export const selectUnblockPinOfflineStatus = (state: RootState) =>
  state.cards.unblockPinOfflineStatus;

export default cardsSlice.reducer;
